import { AuthApi, type CharacterSummary } from '../api/AuthApi';
import { CHANGELOG } from '@shared/data/changelog';
import { ColyseusClient } from '../network/ColyseusClient';
import type { DomLoadingBar } from './loadingBarDom';
import { skinScrollable } from './customScrollbar';
import Phaser from 'phaser';
import { WEB_URL, WIKI_URL, DISCORD_URL, GAME_FONT_FAMILY } from '@shared/constants';
import { MapBackgroundRenderer } from './MapBackgroundRenderer';
import { DevToolsLauncher } from './auth/DevToolsLauncher';
import { applyIconToDom } from '../sprites/IconAtlases';
import { renderLoginForm, renderRegisterForm } from './auth/authForms';
import { AuthChrome } from './auth/AuthChrome';
import { CharacterPicker } from './auth/characterPicker';
import { setLoadingMusicButtonVisible } from '../audio/loadingMusic';
import { loadingLogoWidthCss, LOGO_GLOW_FILTER } from './loadingLayout';

export interface AuthResult {
  sessionToken: string;
  character: CharacterSummary;
}

export type CharacterPickerResult =
  | { type: 'selected'; character: CharacterSummary }
  | { type: 'logout' };

export class AuthUI {
  public readonly api: AuthApi;
  public root: HTMLDivElement;
  public contentLayer: HTMLDivElement;
  /** Modal "Novedades" (changelog): se abre a demanda desde el botón del login. */
  private changelogModal: HTMLDivElement | null = null;
  public mapBackgroundContainer: HTMLDivElement | null = null;
  public mapBackgroundRenderer: MapBackgroundRenderer | null = null;
  /** Lanzador lazy de los editores de dev (extraído a client/src/ui/auth/DevToolsLauncher.ts). */
  private devTools: DevToolsLauncher | null = null;
  /** Chrome visual (frame/logo/fondos/loading/diálogos), extraído a auth/AuthChrome.ts. */
  public chrome: AuthChrome = new AuthChrome(this);
  /** Pantallas de selección/creación de personaje, extraídas a auth/characterPicker.ts. */
  private picker: CharacterPicker = new CharacterPicker(this);
  public static readonly MAX_CHARACTERS = 5;
  // Elige la pantalla de selección de personaje: `true` = versión skineada nueva
  // (arte de `ui/charlist`, ver renderCharacterPickerScreenSkinned); `false` = la
  // versión WoW original (renderCharacterPickerScreen). Flip de una línea para revertir.
  public static readonly USE_SKINNED_CHARLIST = true;
  public currentSessionToken: string | null = null;
  public statusClient: ColyseusClient | null = null;
  public readonly serverUrl: string;
  private loginDoneCallback: ((sessionToken: string) => void) | null = null;

  constructor(api: AuthApi, serverUrl: string = 'ws://localhost:9000') {
    this.api = api;
    this.serverUrl = serverUrl;
    const { root, contentLayer } = this.createRoot();
    this.root = root;
    this.contentLayer = contentLayer;
  }

  async run(): Promise<
    AuthResult & {
      destroyUI: () => void;
      setTransitionProgress: (p: number) => void;
      revealGame: () => Promise<void>;
    }
  > {
    document.body.appendChild(this.root);

    // Mostrar indicador de carga mientras se precarga el fondo
    this.chrome.showLoadingIndicator();

    // Inicializar el fondo del mapa y esperar a que esté listo antes de mostrar la UI
    await this.chrome.initMapBackground();

    // Ocultar indicador de carga
    this.chrome.hideLoadingIndicator();

    const sessionToken = await this.loginOrRegisterFlow();
    this.currentSessionToken = sessionToken;
    const character = await this.pickOrCreateCharacter(sessionToken);

    // Invalidar la sesión en el backend una vez que ya tenemos personaje seleccionado.
    // De esta forma, cada vez que el jugador vuelva al juego deberá hacer login nuevo.
    try {
      await this.api.logout(sessionToken);
    } catch {
      // Si el logout falla, simplemente dejamos que el token expire por TTL en el servidor.
    }

    // NO tapamos con una pantalla negra: al elegir personaje, `enterGame` ya
    // transformó el botón "Entrar" en una barra de carga y la pantalla de
    // selección sigue visible (fondo de mapa + muñeco respirando). El juego
    // carga por detrás; main.ts alimenta la barra vía `setTransitionProgress` y,
    // cuando el mundo está listo, dispara `revealGame` (focus cross-fade) antes
    // de `destroyUI`. Ver docs/AUTH_AND_LOGIN.md.
    return {
      sessionToken,
      character,
      destroyUI: () => this.destroy(),
      setTransitionProgress: (p: number): void => this.transitionBar?.setProgress(p),
      revealGame: (): Promise<void> => this.revealGame(),
    };
  }

  /**
   * Focus cross-fade que revela la ventana del juego desde la pantalla de
   * selección, sin pantalla negra intermedia:
   *  1) La barra que reemplazó al botón "Entrar" se completa.
   *  2) Se desvanecen los elementos de selección (contentLayer + overlay + glow).
   *  3) Se desvanece el fondo de mapa fullscreen (#auth-map-bg): como el auth-root
   *     está por encima de #game-container, al irse revela la ventana del juego
   *     (ya con el mundo cargado) centrada sobre el fondo oscuro del body.
   * La promesa resuelve cuando terminó el fade del fondo. Ver docs/AUTH_AND_LOGIN.md.
   */
  private async revealGame(): Promise<void> {
    const UI_FADE_MS = 350;
    const BG_FADE_MS = 500;

    this.transitionBar?.setProgress(1);

    // 1) Fade-out de los elementos de selección (queda solo el fondo de mapa).
    const uiLayers: HTMLElement[] = [this.contentLayer];
    const overlay = this.root.querySelector<HTMLElement>('#auth-overlay');
    const glow = this.root.querySelector<HTMLElement>('#auth-glow');
    if (overlay) uiLayers.push(overlay);
    if (glow) uiLayers.push(glow);
    for (const el of uiLayers) {
      el.style.transition = `opacity ${UI_FADE_MS}ms ease-out`;
      el.style.opacity = '0';
    }
    await new Promise<void>((r) => window.setTimeout(r, UI_FADE_MS));

    // 2) Fade-out del fondo de mapa → revela la ventana del juego por debajo.
    const bg = this.mapBackgroundContainer;
    if (bg) {
      bg.style.transition = `opacity ${BG_FADE_MS}ms ease-out`;
      bg.style.opacity = '0';
    }
    // El auth-root en sí también se funde para llevar cualquier resto (viñeta base)
    // al negro/transparente de forma pareja con el fondo.
    this.root.style.transition = `opacity ${BG_FADE_MS}ms ease-out`;
    this.root.style.opacity = '0';
    await new Promise<void>((r) => window.setTimeout(r, BG_FADE_MS));
  }

  private createRoot(): { root: HTMLDivElement; contentLayer: HTMLDivElement } {
    const root = document.createElement('div');
    root.id = 'auth-ui-root';
    root.style.position = 'fixed';
    root.style.inset = '0';
    root.style.display = 'flex';
    root.style.flexDirection = 'column';
    root.style.alignItems = 'center';
    root.style.justifyContent = 'center';
    root.style.zIndex = '9999';
    root.style.color = '#eee';
    root.style.fontFamily = 'system-ui, sans-serif';
    root.style.overflow = 'hidden';

    // ===== Fondo con mapa renderizado (Phaser) =====
    const mapBackgroundContainer = document.createElement('div');
    mapBackgroundContainer.id = 'auth-map-bg';
    mapBackgroundContainer.style.position = 'absolute';
    mapBackgroundContainer.style.inset = '0';
    mapBackgroundContainer.style.zIndex = '0';
    mapBackgroundContainer.style.pointerEvents = 'none';
    this.mapBackgroundContainer = mapBackgroundContainer;

    // NOTA: initMapBackground() se llama desde run() después de añadir al DOM

    // ===== Overlay oscuro con efectos =====
    const overlayLayer = document.createElement('div');
    overlayLayer.id = 'auth-overlay';
    overlayLayer.style.position = 'absolute';
    overlayLayer.style.inset = '0';
    overlayLayer.style.zIndex = '1';
    overlayLayer.style.pointerEvents = 'none';
    // Gradiente radial oscuro con viñeta - efecto dramático
    overlayLayer.style.background = `
      radial-gradient(ellipse at center, 
        rgba(0, 0, 0, 0.05) 0%, 
        rgba(0, 0, 0, 0.4) 50%, 
        rgba(0, 0, 0, 0.75) 100%
      )
    `;

    // ===== Capa de brillo ambiental animado (efecto mágico) =====
    const glowLayer = document.createElement('div');
    glowLayer.id = 'auth-glow';
    glowLayer.style.position = 'absolute';
    glowLayer.style.inset = '0';
    glowLayer.style.zIndex = '2';
    glowLayer.style.pointerEvents = 'none';
    glowLayer.style.opacity = '0.4';
    // Gradiente animado con colores sutiles de fantasía
    glowLayer.style.background = `
      radial-gradient(circle at 30% 70%, 
        rgba(79, 70, 120, 0.3) 0%, 
        transparent 40%
      ),
      radial-gradient(circle at 70% 30%, 
        rgba(60, 90, 120, 0.25) 0%, 
        transparent 35%
      )
    `;
    glowLayer.style.animation = 'authGlowPulse 8s ease-in-out infinite alternate';

    // ===== Inyectar CSS para las animaciones =====
    if (!document.getElementById('auth-ui-animations')) {
      const styleSheet = document.createElement('style');
      styleSheet.id = 'auth-ui-animations';
      styleSheet.textContent = `
        @keyframes authGlowPulse {
          0% {
            opacity: 0.2;
            transform: scale(1);
          }
          50% {
            opacity: 0.5;
          }
          100% {
            opacity: 0.3;
            transform: scale(1.05);
          }
        }
        
        /* Efecto de partículas flotantes con pseudo-elementos */
        #auth-ui-root::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-image: 
            radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.03) 0%, transparent 0.5%),
            radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.02) 0%, transparent 0.4%),
            radial-gradient(circle at 40% 40%, rgba(255, 255, 255, 0.02) 0%, transparent 0.3%),
            radial-gradient(circle at 60% 60%, rgba(255, 255, 255, 0.03) 0%, transparent 0.5%),
            radial-gradient(circle at 90% 90%, rgba(255, 255, 255, 0.02) 0%, transparent 0.4%);
          z-index: 3;
          pointer-events: none;
          animation: authParticleFloat 15s ease-in-out infinite;
        }
        
        @keyframes authParticleFloat {
          0%, 100% {
            transform: translateY(0) translateX(0);
          }
          25% {
            transform: translateY(-10px) translateX(5px);
          }
          50% {
            transform: translateY(-5px) translateX(-5px);
          }
          75% {
            transform: translateY(-15px) translateX(3px);
          }
        }
      `;
      document.head.appendChild(styleSheet);
    }

    // Contenedor para el contenido (encima de todos los efectos)
    const contentLayer = document.createElement('div');
    contentLayer.id = 'auth-content';
    contentLayer.style.position = 'relative';
    contentLayer.style.zIndex = '10';
    contentLayer.style.display = 'flex';
    contentLayer.style.flexDirection = 'column';
    contentLayer.style.alignItems = 'center';
    contentLayer.style.justifyContent = 'center';
    contentLayer.style.width = '100%';
    contentLayer.style.height = '100%';

    // ===== Overlay de cross-fade del fondo =====
    // Negro, encima del mapa + capas atmosféricas (z 0..3) pero DEBAJO de la UI
    // (contentLayer z 10). Al cambiar de fondo (login→selección, o al elegir otro
    // personaje) se funde a negro y vuelve, sin tapar el contenido. Ver changeBackground.
    const bgFadeOverlay = document.createElement('div');
    bgFadeOverlay.id = 'auth-bg-fade';
    bgFadeOverlay.style.position = 'absolute';
    bgFadeOverlay.style.inset = '0';
    bgFadeOverlay.style.zIndex = '5';
    bgFadeOverlay.style.pointerEvents = 'none';
    bgFadeOverlay.style.background = '#000000';
    bgFadeOverlay.style.opacity = '0';
    this.bgFadeOverlay = bgFadeOverlay;

    root.appendChild(mapBackgroundContainer);
    root.appendChild(overlayLayer);
    root.appendChild(glowLayer);
    root.appendChild(bgFadeOverlay);
    root.appendChild(contentLayer);

    return { root, contentLayer };
  }

  /** Overlay negro para el cross-fade del fondo (ver changeBackground). */
  public bgFadeOverlay: HTMLDivElement | null = null;

  /** Token incremental para que el cambio de fondo más reciente gane ante clicks rápidos. */
  public bgTransitionSeq = 0;


  public loadingIndicator: HTMLDivElement | null = null;
  public loadingBar: DomLoadingBar | null = null;

  /**
   * Barra de carga que reemplaza al botón "Entrar al personaje" durante la
   * transición al juego (focus cross-fade). La alimenta main.ts con el progreso
   * real del juego vía `setTransitionProgress`. Ver docs/AUTH_AND_LOGIN.md.
   */
  public transitionBar: DomLoadingBar | null = null;
  /**
   * Limpieza diferida del char picker cuando se entró al juego: se mantiene el
   * preview respirando durante la carga y se destruye recién en `destroy()`.
   */
  public deferredPickerCleanup: (() => void) | null = null;

  private destroy(): void {
    // Limpieza diferida del char picker (preview que quedó respirando durante la carga).
    if (this.deferredPickerCleanup) {
      this.deferredPickerCleanup();
      this.deferredPickerCleanup = null;
    }
    this.transitionBar = null;
    // Limpiar el renderizador del mapa de fondo
    if (this.mapBackgroundRenderer) {
      this.mapBackgroundRenderer.destroy();
      this.mapBackgroundRenderer = null;
    }
    this.root.remove();
  }

  public async loginOrRegisterFlow(): Promise<string> {
    return new Promise<string>((resolve) => {
      this.renderLoginRegister((sessionToken) => {
        resolve(sessionToken);
      });
    });
  }

  /**
   * Lanzador lazy de los editores de dev. Se instancia la primera vez que se abre un editor,
   * pasándole el contrato mínimo (contentLayer + volver a login). Ver DevToolsLauncher.
   */
  private getDevTools(): DevToolsLauncher {
    if (!this.devTools) {
      this.devTools = new DevToolsLauncher({
        contentLayer: this.contentLayer,
        hasLoginCallback: () => this.loginDoneCallback !== null,
        returnToLogin: () => this.renderLoginRegister(this.loginDoneCallback!),
      });
    }
    return this.devTools;
  }

  private renderLoginRegister(onDone: (sessionToken: string) => void): void {
    this.loginDoneCallback = onDone;
    this.contentLayer.innerHTML = '';

    // El botón flotante de música sólo se muestra en el loading inicial y el login. Al volver
    // al login (logout desde el picker) lo re-mostramos; el picker/creación lo ocultan.
    setLoadingMusicButtonVisible(true);

    // Marco más fino (36px de borde en vez de 64px) para que el box de login/registro
    // no deje tanto padding alrededor del formulario.
    const { root: container, content: panel } = this.chrome.createWindowFrame(36);
    // Ancho del área central (sin contar el marco)
    panel.style.width = '320px'; // Ancho fijo para que login y registro tengan el mismo ancho
    panel.style.display = 'flex';
    panel.style.flexDirection = 'column';
    panel.style.alignItems = 'center';
    panel.style.justifyContent = 'flex-start';

    // ===== Logo: ahora va FUERA del box, arriba, centrado en el espacio disponible =====
    const logoContainer = document.createElement('div');
    logoContainer.style.marginBottom = '12px';
    logoContainer.style.display = 'flex';
    logoContainer.style.justifyContent = 'center';
    logoContainer.style.alignItems = 'center';
    // Sin “placa”/bullet detrás: el logo se limpia con el preprocesado (canvas) para
    // eliminar los píxeles blancos del contorno pegados a transparencia.

    const logo = document.createElement('img');
    logo.src = '/assets/logo.png';
    logo.alt = 'Aureum Logo';
    // El logo (AUREUM ONLINE) es un render suave hi-res: se escala con interpolación
    // suave del browser (imageRendering auto), igual que en la pantalla de loading.
    const LOGO_TARGET_WIDTH_PX = 380;
    // Mismo tamaño que el logo de las pantallas de loading (fuente única: loadingLayout).
    logo.style.width = loadingLogoWidthCss();
    logo.style.height = 'auto';
    logo.style.objectFit = 'contain';
    logo.style.display = 'block';
    (logo.style as any).imageRendering = 'auto';
    // Glow negro externo (fuente única: loadingLayout) — mismo en todas las pantallas de login.
    logo.style.filter = LOGO_GLOW_FILTER;

    // Reemplazar el src por una versión sin halo + escalada sin smoothing (async, no bloquea la UI)
    void this.chrome.getLogoUrlNearestScaled(LOGO_TARGET_WIDTH_PX).then((url) => {
      // Si el elemento sigue vivo, aplicamos
      if (logo.isConnected) {
        logo.src = url;
      }
    });

    logoContainer.appendChild(logo);

    const formContainer = document.createElement('div');
    formContainer.style.width = '100%';

    panel.appendChild(formContainer);

    // Stack vertical centrado por contentLayer (flex column center): [logo] [box] [links].
    // Al sacar el logo del box, el conjunto queda equilibrado verticalmente.
    this.contentLayer.appendChild(logoContainer);
    this.contentLayer.appendChild(container);

    // ===== Botones públicos (debajo del box): Wiki / Discord / Web =====
    // Íconos SVG inline de Tabler Icons (outline, MIT): misma familia (stroke 2,
    // round) para que combinen entre sí. Se tiñen con `currentColor`.
    const tablerSvg = (inner: string): string =>
      `<svg viewBox="0 0 24 24" width="100%" height="100%" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${inner}</svg>`;
    const ICON_WIKI = tablerSvg(
      '<path d="M3 19a9 9 0 0 1 9 0a9 9 0 0 1 9 0" /><path d="M3 6a9 9 0 0 1 9 0a9 9 0 0 1 9 0" /><path d="M3 6l0 13" /><path d="M12 6l0 13" /><path d="M21 6l0 13" />'
    );
    const ICON_DISCORD = tablerSvg(
      '<path d="M8 12a1 1 0 1 0 2 0a1 1 0 0 0 -2 0" /><path d="M14 12a1 1 0 1 0 2 0a1 1 0 0 0 -2 0" /><path d="M15.5 17c0 1 1.5 3 2 3c1.5 0 2.833 -1.667 3.5 -3c.667 -1.667 .5 -5.833 -1.5 -11.5c-1.457 -1.015 -3 -1.34 -4.5 -1.5l-1 2.5" /><path d="M7 16.5c-1.5 0 -3 -.5 -4 -2c.667 -5.667 2 -9.833 4 -11.5c1.457 -1.015 3 -1.34 4.5 -1.5l1 2.5" />'
    );
    const ICON_WEB = tablerSvg(
      '<path d="M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0" /><path d="M3.6 9h16.8" /><path d="M3.6 15h16.8" /><path d="M11.5 3a17 17 0 0 0 0 18" /><path d="M12.5 3a17 17 0 0 1 0 18" />'
    );

    // Botón flotante (sin borde ni fondo): ícono + palabra en MAYÚSCULA a la derecha, con un
    // acento "gema" de RPG (oro/amatista/esmeralda) que arma juego con la paleta dorada del juego.
    // `accent` es un hex de 6 dígitos; se le agrega canal alpha (8 dígitos) para el glow del hover.
    const makeLinkButton = (
      label: string,
      svg: string,
      url: string,
      accent: string
    ): HTMLButtonElement => {
      const btn = document.createElement('button');
      btn.setAttribute('aria-label', label);
      btn.style.display = 'flex';
      btn.style.alignItems = 'center';
      btn.style.gap = '8px';
      btn.style.padding = '6px 8px';
      btn.style.cursor = 'pointer';
      btn.style.border = 'none';
      btn.style.background = 'none';
      btn.style.color = accent;
      btn.style.opacity = '0.9';
      btn.style.filter = 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.6))';
      btn.style.transition = 'opacity 0.2s, transform 0.2s, filter 0.2s';

      const iconSpan = document.createElement('span');
      iconSpan.innerHTML = svg;
      iconSpan.style.width = '18px';
      iconSpan.style.height = '18px';
      iconSpan.style.display = 'flex';
      iconSpan.style.alignItems = 'center';
      iconSpan.style.justifyContent = 'center';
      iconSpan.style.flexShrink = '0';

      const labelSpan = document.createElement('span');
      labelSpan.textContent = label.toUpperCase();
      labelSpan.style.fontSize = '13px';
      labelSpan.style.fontWeight = '700';
      labelSpan.style.letterSpacing = '1.5px';
      labelSpan.style.lineHeight = '1';

      btn.append(iconSpan, labelSpan);

      btn.onmouseenter = () => {
        btn.style.opacity = '1';
        btn.style.transform = 'translateY(-2px)';
        btn.style.filter = `drop-shadow(0 4px 8px rgba(0, 0, 0, 0.6)) drop-shadow(0 0 6px ${accent}99)`;
      };
      btn.onmouseleave = () => {
        btn.style.opacity = '0.9';
        btn.style.transform = 'translateY(0)';
        btn.style.filter = 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.6))';
      };
      btn.onclick = () => {
        window.open(url, '_blank', 'noopener');
      };
      return btn;
    };

    // Acentos tipo gema, dentro de la paleta cálida/fantástica del juego.
    const ACCENT_GOLD = '#FFD700'; // oro (Wiki)
    const ACCENT_AMETHYST = '#A78BFA'; // amatista (Discord)
    const ACCENT_EMERALD = '#34D399'; // esmeralda (Web)

    const linksContainer = document.createElement('div');
    linksContainer.style.display = 'flex';
    linksContainer.style.justifyContent = 'center';
    linksContainer.style.flexWrap = 'wrap';
    linksContainer.style.gap = '14px';
    linksContainer.style.marginTop = '20px';
    linksContainer.appendChild(makeLinkButton('Wiki', ICON_WIKI, WIKI_URL, ACCENT_GOLD));
    linksContainer.appendChild(makeLinkButton('Discord', ICON_DISCORD, DISCORD_URL, ACCENT_AMETHYST));
    linksContainer.appendChild(makeLinkButton('Web', ICON_WEB, WEB_URL, ACCENT_EMERALD));
    this.contentLayer.appendChild(linksContainer);

    // Botón "Novedades" → abre el modal del changelog (no navega a una URL). En su
    // PROPIA fila, debajo de Wiki/Discord/Web.
    const novedadesRow = document.createElement('div');
    novedadesRow.style.display = 'flex';
    novedadesRow.style.justifyContent = 'center';
    novedadesRow.style.marginTop = '6px';
    const novedadesBtn = makeLinkButton('Novedades', '📜', '', ACCENT_GOLD);
    novedadesBtn.onclick = (e) => {
      e.preventDefault();
      this.openChangelogModal();
    };
    novedadesRow.appendChild(novedadesBtn);
    this.contentLayer.appendChild(novedadesRow);

    // El changelog YA NO auto-abre como modal en el login: las Novedades ahora viven en un
    // panel compacto a la izquierda del char-select (renderCharacterPickerScreenSkinned).
    // El botón "Novedades" de arriba sigue disponible para abrir el modal a demanda.

    // ===== Botones de herramientas de dev (abajo a la izquierda, gated) =====
    // Visibles solo en dev local (Vite) o si se activó `localStorage.dev = 'true'` en la
    // consola del navegador (persiste). En producción están ocultos por defecto.
    const showDevTools =
      import.meta.env.DEV || window.localStorage.getItem('dev') === 'true';

    if (showDevTools) {
      const makeDevToolButton = (
        iconName: string,
        fallbackEmoji: string,
        title: string,
        description: string,
        onClick: () => void
      ): HTMLButtonElement => {
        const btn = document.createElement('button');
        btn.style.position = 'relative';
        btn.style.width = '40px';
        btn.style.height = '40px';
        btn.style.padding = '0';
        btn.style.cursor = 'pointer';
        btn.style.borderRadius = '50%';
        btn.style.border = 'none';
        btn.style.background = 'rgba(68, 68, 68, 0.7)';
        btn.style.color = '#eee';
        btn.style.fontSize = '20px';
        btn.style.display = 'flex';
        btn.style.alignItems = 'center';
        btn.style.justifyContent = 'center';
        btn.style.transition = 'background 0.2s';

        // Ícono del atlas ui_icons_3 (icons3.png) en vez del emoji; si el nombre no resuelve,
        // fallback al emoji para no quedar sin ícono. Ver client/src/sprites/IconAtlases.ts.
        const iconEl = document.createElement('span');
        iconEl.style.display = 'block';
        iconEl.style.pointerEvents = 'none';
        if (applyIconToDom(iconEl, iconName, 26)) {
          btn.appendChild(iconEl);
        } else {
          btn.textContent = fallbackEmoji;
        }

        // Tooltip propio: título en bold (prioridad visual) + descripción en texto light debajo.
        const tooltip = document.createElement('div');
        tooltip.style.position = 'absolute';
        tooltip.style.left = '0';
        tooltip.style.bottom = 'calc(100% + 8px)';
        tooltip.style.minWidth = '180px';
        tooltip.style.maxWidth = '240px';
        tooltip.style.padding = '8px 10px';
        tooltip.style.background = 'rgba(0, 0, 0, 0.9)';
        tooltip.style.border = '1px solid rgba(255, 215, 0, 0.4)';
        tooltip.style.borderRadius = '6px';
        tooltip.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.5)';
        tooltip.style.textAlign = 'left';
        tooltip.style.pointerEvents = 'none';
        tooltip.style.opacity = '0';
        tooltip.style.transition = 'opacity 0.15s';
        tooltip.style.zIndex = '30';

        const tooltipTitle = document.createElement('div');
        tooltipTitle.textContent = title;
        tooltipTitle.style.fontWeight = '700';
        tooltipTitle.style.fontSize = '13px';
        tooltipTitle.style.color = '#FFD700';
        tooltipTitle.style.lineHeight = '1.2';

        const tooltipDesc = document.createElement('div');
        tooltipDesc.textContent = description;
        tooltipDesc.style.fontWeight = '400';
        tooltipDesc.style.fontSize = '11px';
        tooltipDesc.style.color = '#eee';
        tooltipDesc.style.opacity = '0.75';
        tooltipDesc.style.marginTop = '3px';
        tooltipDesc.style.lineHeight = '1.3';

        tooltip.append(tooltipTitle, tooltipDesc);
        btn.appendChild(tooltip);

        btn.onmouseenter = () => {
          btn.style.background = 'rgba(68, 68, 68, 0.95)';
          tooltip.style.opacity = '1';
        };
        btn.onmouseleave = () => {
          btn.style.background = 'rgba(68, 68, 68, 0.7)';
          tooltip.style.opacity = '0';
        };
        btn.onclick = onClick;
        return btn;
      };

      const devToolsBar = document.createElement('div');
      devToolsBar.style.position = 'absolute';
      devToolsBar.style.left = '16px';
      devToolsBar.style.bottom = '16px';
      devToolsBar.style.display = 'flex';
      // Columna vertical apilada desde abajo-izquierda (antes fila horizontal).
      devToolsBar.style.flexDirection = 'column-reverse';
      devToolsBar.style.alignItems = 'flex-start';
      devToolsBar.style.gap = '10px';
      devToolsBar.style.zIndex = '20';
      devToolsBar.appendChild(
        makeDevToolButton(
          'tool_tree',
          '🌳',
          'Editor de árbol de pasivas',
          'Diseñá nodos y conexiones del árbol de Espíritu.',
          () => this.getDevTools().openPassiveTreeEditor()
        )
      );
      devToolsBar.appendChild(
        makeDevToolButton(
          'tool_sprite',
          '🖼️',
          'Editor de frames de sprites',
          'Definí frames y recortes de spritesheets.',
          () => this.getDevTools().openSpriteFrameEditor()
        )
      );
      devToolsBar.appendChild(
        makeDevToolButton(
          'tool_surface',
          '🎨',
          'Indexador de superficies',
          'Indexá y catalogá tiles de superficie.',
          () => this.getDevTools().openSurfaceIndexer()
        )
      );
      devToolsBar.appendChild(
        makeDevToolButton(
          'tool_particle',
          '✨',
          'Editor de partículas',
          'Creá y ajustá efectos de partículas.',
          () => this.getDevTools().openParticleEditor()
        )
      );
      devToolsBar.appendChild(
        makeDevToolButton(
          'skill_construction',
          '🏠',
          'Bloqueos de casas',
          'Definí la colisión por tile de cada casa.',
          () => this.getDevTools().openHouseCollisionEditor()
        )
      );
      devToolsBar.appendChild(
        makeDevToolButton('map', '🗺️', 'Editor de mapas', 'Cargá y editá mapas: tiles, NPCs e items.', () =>
          this.getDevTools().openMapEditor()
        )
      );
      devToolsBar.appendChild(
        makeDevToolButton('tool_world', '🌍', 'World Builder', 'Pintá la base macro del mundo y generá el mapa.', () =>
          this.getDevTools().openWorldBuilder()
        )
      );
      this.contentLayer.appendChild(devToolsBar);
    }

    // Estado para saber qué formulario mostrar
    let currentForm: 'login' | 'register' = 'login';

    const showForm = (form: 'login' | 'register'): void => {
      currentForm = form;
      if (form === 'login') {
        renderLoginForm(this.api, formContainer, onDone, () => showForm('register'));
      } else {
        renderRegisterForm(this.api, formContainer, onDone, () => showForm('login'));
      }
    };

    showForm('login');
  }

  /**
   * Arma el cuerpo scrollable del changelog (recorrido de CHANGELOG con resaltado de
   * conceptos `**...**` + links a la wiki). Reutilizado por el modal del login y por el
   * panel compacto de Novedades del char-select. `compact` achica fuentes/padding para el
   * panel lateral ("formato mas chico"). Ver shared/data/changelog.ts.
   */
  public buildChangelogBody(compact = false): HTMLDivElement {
    const dateSize = compact ? 14 : 17;
    const lineSize = compact ? 12.5 : 15;
    const body = document.createElement('div');
    body.style.cssText = `padding:${compact ? '4px 10px 10px' : '8px 18px 16px'};overflow-y:auto;`;
    for (const entry of CHANGELOG) {
      const dateEl = document.createElement('div');
      dateEl.textContent = entry.date;
      dateEl.style.cssText = `color:#c8b070;font-family:${GAME_FONT_FAMILY};font-size:${dateSize}px;margin:${compact ? '8px 0 3px' : '12px 0 4px'};letter-spacing:1px;`;
      body.appendChild(dateEl);
      const ul = document.createElement('ul');
      ul.style.cssText = `margin:0;padding-left:${compact ? '15px' : '18px'};`;
      for (const line of entry.entries) {
        const li = document.createElement('li');
        li.style.cssText = `color:#cfcfcf;font-family:${GAME_FONT_FAMILY};font-size:${lineSize}px;line-height:1.3;margin-bottom:2px;`;
        // Resaltar SÓLO el concepto clave de cada ítem (dorado + bold), marcado en el
        // texto con **...**. La gente escanea conceptos, no lee el párrafo entero.
        const parts = line.split('**');
        parts.forEach((part, i) => {
          if (!part) return;
          if (i % 2 === 1) {
            // Concepto resaltado. Con `|pagina#ancla` → link a la wiki (interactivo);
            // sin `|` → sólo dorado+bold.
            const pipe = part.indexOf('|');
            if (pipe > 0) {
              const a = document.createElement('a');
              a.textContent = part.slice(0, pipe);
              a.href = `${WIKI_URL}/${part.slice(pipe + 1)}`;
              a.target = '_blank';
              a.rel = 'noopener';
              a.style.cssText =
                'font-weight:700;color:#f2d98a;text-decoration:none;cursor:pointer;';
              a.onmouseenter = (): void => {
                a.style.textDecoration = 'underline';
              };
              a.onmouseleave = (): void => {
                a.style.textDecoration = 'none';
              };
              li.appendChild(a);
            } else {
              const strong = document.createElement('span');
              strong.textContent = part;
              strong.style.cssText = 'font-weight:700;color:#f2d98a;';
              li.appendChild(strong);
            }
          } else {
            li.appendChild(document.createTextNode(part));
          }
        });
        ul.appendChild(li);
      }
      body.appendChild(ul);
    }
    skinScrollable(body);
    return body;
  }

  /** Modal flotante "Novedades" (changelog público). Ver shared/data/changelog.ts. */
  private openChangelogModal(): void {
    this.closeChangelogModal();

    const overlay = document.createElement('div');
    overlay.style.cssText =
      'position:fixed;inset:0;z-index:1000;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,0.6);';
    overlay.onclick = (e) => {
      if (e.target === overlay) this.closeChangelogModal();
    };

    const box = document.createElement('div');
    // Ancho del modal de Novedades del LOGIN: el doble que antes (520 → 1040), con tope 90vw
    // para pantallas chicas. (El panel de Novedades del picker de personajes es aparte y no cambia.)
    box.style.cssText =
      'width:min(1040px,90vw);max-height:80vh;display:flex;flex-direction:column;background:linear-gradient(#1a1712,#0e0c09);border:2px solid #6b5a2e;border-radius:10px;box-shadow:0 10px 40px rgba(0,0,0,0.7),0 0 20px rgba(255,215,0,0.15);overflow:hidden;';

    const header = document.createElement('div');
    header.style.cssText =
      'display:flex;align-items:center;justify-content:space-between;padding:14px 18px;border-bottom:1px solid #4a3f22;';
    const title = document.createElement('h2');
    title.textContent = 'NOVEDADES';
    title.style.cssText = `margin:0;color:#FFD700;font-family:${GAME_FONT_FAMILY};font-size:22px;letter-spacing:2px;`;
    const closeBtn = document.createElement('button');
    closeBtn.textContent = '✕';
    closeBtn.style.cssText =
      'background:none;border:none;color:#c8b070;font-size:20px;cursor:pointer;line-height:1;';
    closeBtn.onclick = () => this.closeChangelogModal();
    header.append(title, closeBtn);

    const body = this.buildChangelogBody();

    box.append(header, body);
    overlay.appendChild(box);
    this.root.appendChild(overlay);
    this.changelogModal = overlay;
  }

  private closeChangelogModal(): void {
    if (this.changelogModal) {
      this.changelogModal.remove();
      this.changelogModal = null;
    }
  }

  private async pickOrCreateCharacter(sessionToken: string): Promise<CharacterSummary> {
    // Fuera del login: ocultar el botón flotante de música (el audio se maneja desde Opciones).
    setLoadingMusicButtonVisible(false);
    const characters = await this.api.getCharacters(sessionToken);
    const selected = await this.picker.showCharacterPicker(sessionToken, characters);
    return selected;
  }

}




